/**
 * Every word the Build tab's "What happens to the answer" says — Story 20.7.
 *
 * Kept apart by WHERE EACH WORD COMES FROM, as `extraction-copy.ts` does for
 * the Qualify tab, because each kind is held to a different source by
 * `tests/answer-mode-copy.test.ts`:
 *
 *   DRAWN     `design/screens/editor-build.html` draws it — the `long-text`
 *             capture and its two specimens. Held to the mockup, verbatim.
 *   COMPOSED  no capture draws it: one field where the drawing has three.
 *             Built on the drawn sentence's own shape, and listed here so a
 *             round-trip replaces them in one place.
 *
 * All of it is held to RULE-021 by the same test: the control says what happens
 * to an answer, never whether an answer is good.
 *
 * NO RUNTIME IMPORTS, so a node-side test can load this file without resolving
 * the workspace.
 */

export const DRAWN = {
  /** The control's label, and the name of its group. */
  label: 'What happens to the answer',
  /** `capture_only` (PRD §3.6). */
  keep: 'Keep it with the lead',
  /** `ai_extraction` (PRD §3.6). */
  pull: 'Pull facts out of it',
  /** Under the control when `capture_only` is chosen — the "new question" specimen. */
  keepLine: 'Stored and shown with the lead. It is never read for facts, and never scored.',
  /** Under the control when `ai_extraction` is chosen. */
  pullLine: 'Name the facts to pull out of it on the Qualify tab. Each one is a typed field a criterion can score.',
  /** "Read by 3 fields: experience_years, site_constraints and urgency_reason." */
  readBy: (count: number, keys: string): string => `Read by ${count} fields: ${keys}.`,
  /** The quiet link under the fields. */
  openQualify: 'Open the Qualify tab',
  /** The "Refused before anything is saved." specimen, in the input's error treatment. */
  refused: (keys: string): string => `${keys} read this answer. Remove those fields on the Qualify tab first.`,
} as const

export const COMPOSED = {
  /** One field reads the answer — the drawing's sentence, in the singular. */
  readByOne: (key: string): string => `Read by 1 field: ${key}.`,
  refusedOne: (key: string): string => `${key} reads this answer. Remove that field on the Qualify tab first.`,
} as const
